<template>
    <div class="auth-layout">

        <!-- Panel izquierdo: Marca -->
        <div class="auth-brand">
            <div class="brand-content">
                <img src="@/assets/img/logo_login1.png" alt="ANW Logistics" class="brand-logo" />
                <h2 class="brand-title">Bienvenido de nuevo</h2>
                <p class="brand-sub">Gestiona tus envíos locales y provinciales desde un solo lugar.</p>
                <div class="brand-features">
                    <div class="brand-feature"><i class="bi bi-truck"></i> Envíos en tiempo real</div>
                    <div class="brand-feature"><i class="bi bi-shield-check"></i> Plataforma segura</div>
                    <div class="brand-feature"><i class="bi bi-graph-up"></i> Trazabilidad completa</div>
                </div>
            </div>
            <div class="brand-deco deco-1"></div>
            <div class="brand-deco deco-2"></div>
        </div>

        <!-- Panel derecho: Formulario -->
        <div class="auth-form-panel">
            <div class="auth-form-inner">

                <div class="auth-form-header">
                    <img src="@/assets/img/logo_login1.png" alt="ANW" class="mobile-logo" />
                    <h3 class="auth-title">Iniciar sesión</h3>
                    <p class="auth-sub">Ingresa tus credenciales para continuar</p>
                </div>

                <form @submit.prevent="login">

                    <div class="field-group">
                        <label class="field-label">Correo electrónico</label>
                        <div class="field-input" :class="{ focused: focusEmail }">
                            <i class="bi bi-envelope field-icon"></i>
                            <input type="email" v-model="email" placeholder="tu@correo.com" required
                                @focus="focusEmail = true" @blur="focusEmail = false" />
                        </div>
                    </div>

                    <div class="field-group">
                        <div class="field-label-row">
                            <label class="field-label">Contraseña</label>
                            <a href="#" @click.prevent="mostrarRecuperarPassword" class="forgot-link">¿Olvidaste tu contraseña?</a>
                        </div>
                        <div class="field-input" :class="{ focused: focusPass }">
                            <i class="bi bi-lock field-icon"></i>
                            <input :type="showPassword ? 'text' : 'password'" v-model="password" placeholder="••••••••" required
                                @focus="focusPass = true" @blur="focusPass = false" />
                            <button type="button" class="eye-btn" @click="showPassword = !showPassword">
                                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                            </button>
                        </div>
                    </div>

                    <p v-if="error" class="auth-error"><i class="bi bi-exclamation-circle me-1"></i>{{ error }}</p>

                    <button type="submit" class="auth-btn" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                        <span v-else><i class="bi bi-box-arrow-in-right me-2"></i></span>
                        {{ loading ? 'Ingresando...' : 'Iniciar sesión' }}
                    </button>

                    <p class="auth-link-row">¿No tienes cuenta? <a href="/register">Regístrate aquí</a></p>
                </form>
            </div>
        </div>

        <!-- Modal recuperar contraseña -->
        <div v-if="mostrarModal" class="auth-modal-bg">
            <div class="auth-modal">
                <div class="auth-modal-header">
                    <h5><i class="bi bi-key me-2"></i>Recuperar Contraseña</h5>
                    <button class="auth-modal-close" @click="cerrarModal"><i class="bi bi-x-lg"></i></button>
                </div>
                <div class="auth-modal-body">
                    <div v-if="!emailEnviado">
                        <p class="text-muted mb-3">Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>
                        <div class="field-group">
                            <label class="field-label">Correo electrónico</label>
                            <div class="field-input">
                                <i class="bi bi-envelope field-icon"></i>
                                <input type="email" v-model="emailRecuperar" placeholder="tu@correo.com" required />
                            </div>
                        </div>
                        <div class="d-flex gap-2 justify-content-end mt-3">
                            <button class="btn btn-secondary btn-sm" @click="cerrarModal">Cancelar</button>
                            <button class="auth-btn-sm" @click="enviarRecuperacion" :disabled="loadingRecuperar">
                                <span v-if="loadingRecuperar" class="spinner-border spinner-border-sm me-1"></span>
                                Enviar enlace
                            </button>
                        </div>
                    </div>
                    <div v-else class="text-center py-3">
                        <i class="bi bi-check-circle-fill text-success" style="font-size:3rem;"></i>
                        <h6 class="mt-3 fw-bold">¡Correo enviado!</h6>
                        <p class="text-muted">Revisa tu bandeja de entrada en <strong>{{ emailRecuperar }}</strong>.</p>
                        <button class="auth-btn-sm" @click="cerrarModal">Entendido</button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import { programarRefresh } from '@/services/authService';
import Swal from 'sweetalert2';

export default {
    name: 'LoginPage',
    data() {
        return {
            email: '',
            password: '',
            error: '',
            loading: false,
            showPassword: false,
            focusEmail: false,
            focusPass: false,
            mostrarModal: false,
            emailRecuperar: '',
            loadingRecuperar: false,
            emailEnviado: false,
        };
    },
    methods: {
        ...mapActions(['setToken']),
        async login() {
            this.loading = true;
            this.error = '';
            try {
                const response = await axios.post('/login', { email: this.email, password: this.password });
                if (response.status === 200) {
                    this.setToken(response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    programarRefresh(response.data.token);
                    if (response.data.user?.cargo?.toLowerCase() == 'admin') {
                        this.$router.push('/dashboard');
                    } else {
                        this.$router.push('/listado-envios-locales');
                    }
                } else {
                    this.error = 'Hubo un problema al iniciar sesión.';
                }
            } catch (err) {
                this.error = err.response?.data?.error || 'Error en el inicio de sesión.';
            } finally {
                this.loading = false;
            }
        },
        mostrarRecuperarPassword() {
            this.mostrarModal = true;
            this.emailRecuperar = '';
            this.emailEnviado = false;
        },
        cerrarModal() {
            this.mostrarModal = false;
            this.emailRecuperar = '';
            this.emailEnviado = false;
            this.loadingRecuperar = false;
        },
        async enviarRecuperacion() {
            this.loadingRecuperar = true;
            try {
                const response = await axios.post('/password/email', { email: this.emailRecuperar });
                if (response.status === 200) this.emailEnviado = true;
            } catch (error) {
                let mensaje = 'Error al enviar el correo de recuperación.';
                if (error.response?.status === 404) mensaje = 'No se encontró una cuenta con ese correo.';
                else if (error.response?.data?.message) mensaje = error.response.data.message;
                Swal.fire({ icon: 'error', title: 'Error', text: mensaje });
            } finally {
                this.loadingRecuperar = false;
            }
        }
    }
};
</script>

<style scoped>
/* ── Layout ── */
.auth-layout {
    display: flex;
    min-height: 100vh;
    width: 100vw;
    overflow: hidden;
}

/* ── Panel de Marca (izquierdo) ── */
.auth-brand {
    flex: 0 0 45%;
    background: linear-gradient(145deg, #3f51b5 0%, #283593 60%, #1a237e 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    padding: 48px 40px;
}
.brand-content {
    position: relative;
    z-index: 2;
    text-align: center;
    max-width: 340px;
}
.brand-logo {
    width: 180px;
    margin-bottom: 36px;
    filter: brightness(0) invert(1);
}
.brand-title {
    color: #fff;
    font-size: 1.6rem;
    font-weight: 800;
    margin-bottom: 12px;
    line-height: 1.2;
}
.brand-sub {
    color: rgba(255,255,255,0.75);
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 36px;
}
.brand-features {
    display: flex;
    flex-direction: column;
    gap: 14px;
    text-align: left;
}
.brand-feature {
    display: flex;
    align-items: center;
    gap: 12px;
    color: rgba(255,255,255,0.9);
    font-size: 0.88rem;
    font-weight: 500;
    background: rgba(255,255,255,0.1);
    padding: 10px 16px;
    border-radius: 10px;
    backdrop-filter: blur(4px);
}
.brand-feature i { font-size: 1.1rem; color: #c5cae9; }

/* Decoraciones circulares */
.brand-deco {
    position: absolute;
    border-radius: 50%;
    opacity: 0.08;
    background: #fff;
}
.deco-1 { width: 350px; height: 350px; top: -100px; right: -100px; }
.deco-2 { width: 250px; height: 250px; bottom: -80px; left: -60px; }

/* ── Panel formulario (derecho) ── */
.auth-form-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9ff;
    padding: 40px 24px;
    overflow-y: auto;
}
.auth-form-inner {
    width: 100%;
    max-width: 400px;
}

/* Logo móvil (solo visible en mobile) */
.mobile-logo {
    display: none;
    width: 120px;
    margin-bottom: 20px;
}

.auth-form-header { margin-bottom: 32px; }
.auth-title {
    font-size: 1.6rem;
    font-weight: 800;
    color: #1a237e;
    margin-bottom: 6px;
}
.auth-sub { color: #78909c; font-size: 0.9rem; }

/* ── Campos ── */
.field-group { margin-bottom: 20px; }
.field-label {
    display: block;
    font-size: 0.78rem;
    font-weight: 700;
    color: #546e7a;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 6px;
}
.field-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}
.forgot-link {
    font-size: 0.78rem;
    color: #3f51b5;
    text-decoration: none;
    font-weight: 600;
}
.forgot-link:hover { text-decoration: underline; }

.field-input {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1.5px solid #e0e0e0;
    border-radius: 10px;
    height: 48px;
    transition: border-color 0.2s, box-shadow 0.2s;
    overflow: hidden;
}
.field-input.focused {
    border-color: #3f51b5;
    box-shadow: 0 0 0 3px rgba(63,81,181,0.12);
}
.field-icon {
    padding: 0 14px;
    color: #9e9e9e;
    font-size: 1rem;
    flex-shrink: 0;
}
.field-input input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.92rem;
    color: #212121;
    height: 100%;
}
.field-input input::placeholder { color: #bdbdbd; }
.eye-btn {
    background: none;
    border: none;
    padding: 0 14px;
    cursor: pointer;
    color: #9e9e9e;
    font-size: 1rem;
    height: 100%;
    display: flex;
    align-items: center;
}
.eye-btn:hover { color: #3f51b5; }

/* ── Error ── */
.auth-error {
    background: #ffebee;
    color: #c62828;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.85rem;
    margin-bottom: 16px;
    border-left: 3px solid #c62828;
}

/* ── Botón principal ── */
.auth-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #3f51b5 0%, #283593 100%);
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: opacity 0.2s, transform 0.15s;
    margin-bottom: 20px;
}
.auth-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.auth-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.auth-link-row {
    text-align: center;
    font-size: 0.88rem;
    color: #78909c;
    margin: 0;
}
.auth-link-row a { color: #3f51b5; font-weight: 700; text-decoration: none; }
.auth-link-row a:hover { text-decoration: underline; }

/* ── Modal ── */
.auth-modal-bg {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 16px;
}
.auth-modal {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    width: 100%;
    max-width: 440px;
    overflow: hidden;
}
.auth-modal-header {
    background: linear-gradient(135deg, #3f51b5 0%, #283593 100%);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #fff;
}
.auth-modal-header h5 { margin: 0; font-size: 1rem; font-weight: 700; }
.auth-modal-close {
    background: rgba(255,255,255,0.2);
    border: none;
    border-radius: 6px;
    color: #fff;
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
}
.auth-modal-close:hover { background: rgba(255,255,255,0.35); }
.auth-modal-body { padding: 24px; }

.auth-btn-sm {
    background: linear-gradient(135deg, #3f51b5 0%, #283593 100%);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 8px 20px;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.auth-btn-sm:hover { opacity: 0.9; }
.auth-btn-sm:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Responsive ── */
@media (max-width: 768px) {
    .auth-brand { display: none; }
    .auth-form-panel {
        background: url('@/assets/img/peru-logistics-bg.png') no-repeat center center;
        background-size: cover;
        align-items: flex-start;
        padding: 24px 16px;
    }
    .auth-form-inner {
        background: rgba(255,255,255,0.97);
        border-radius: 16px;
        padding: 32px 24px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        margin-top: 40px;
    }
    .mobile-logo { display: block; }
    .auth-title { font-size: 1.3rem; }
}
</style>
